import { singleton } from 'tsyringe';
import { OWWindowsListenerDelegate } from '../../odk-ts';
import {
  kNameOfTesterWindowService,
  ITesterWindowService,
} from '../../shared/interfaces/tester-window-service.interface';
import { OWWindowListenerWrapper } from '../../shared/wrappers/ow-window-listener-wrapper';
import { kTesterWindowName } from '../constants';
import {
  ITesterWindowManagerService,
  ITesterWindowManagerServiceDelegate,
} from '../interfaces/tester-window-manager-service.interface';
import { PersistentStorageService } from './persistent-storage-service/persistent-storage.service';
import { Game } from '../models/game.model';
import { Promotion } from '../models/promotion.model';
import { RecommendationDatastoreService } from './datastore-service/recommendation-datastore.service';

// -----------------------------------------------------------------------------
@singleton()
export class TesterWindowService
  implements
    ITesterWindowService,
    ITesterWindowManagerService,
    OWWindowsListenerDelegate
{
  private readonly listeners: Set<ITesterWindowManagerServiceDelegate>;
  private isRunning: boolean;

  public constructor(
    private readonly persistentStorage: PersistentStorageService,
    private readonly owWindowListener: OWWindowListenerWrapper,
    private readonly datastoreService: RecommendationDatastoreService,
  ) {
    this.listeners = new Set<ITesterWindowManagerServiceDelegate>();
    this.isRunning = false;
    this.owWindowListener.addListener(this);
  }

  // ---------------------------------------------------------------------------
  private async triggerListenerEvent(
    game: Game,
    promotion: Promotion,
    useDevEnv: string,
  ) {
    if (!game || !promotion) {
      return;
    }

    for (const listener of this.listeners) {
      await listener.onTestingPromotionSet(game, promotion, useDevEnv);
    }
  }

  // ---------------------------------------------------------------------------
  IsPromotionRunning(): boolean {
    return this.isRunning;
  }

  // ---------------------------------------------------------------------------
  // TesterWindowManagerServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  addListener(listener: ITesterWindowManagerServiceDelegate) {
    this.listeners.add(listener);
  }

  // ---------------------------------------------------------------------------
  removeListener(listener: ITesterWindowManagerServiceDelegate) {
    this.listeners.delete(listener);
  }

  // ---------------------------------------------------------------------------
  // TesterWindowManagerService
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  promotionAlreadyRunning(isRunning: boolean) {
    this.isRunning = isRunning;
  }

  // ---------------------------------------------------------------------------
  // TesterWindowService
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  transferTesterPromotion(
    game: Game,
    promotion: Promotion,
    useDevEnv: string = null,
  ) {
    this.triggerListenerEvent(game, promotion, useDevEnv);
  }

  // ---------------------------------------------------------------------------
  resetPromotionTimestamp() {
    this.persistentStorage.resetLastDisplayedPromotionTimestamp();
  }

  // ---------------------------------------------------------------------------
  resetLocalStorage() {
    this.persistentStorage.deleteLocalStorageItems();
  }

  // ---------------------------------------------------------------------------
  resetPromotionsHistory() {
    this.persistentStorage.createPromotionHistory();
  }

  // ---------------------------------------------------------------------------
  getGlobalConfigurations() {
    return this.datastoreService.getGlobalConfigurations();
  }

  // ---------------------------------------------------------------------------
  getAllGamesConfigurations(): Promise<Game[]> {
    return this.datastoreService.getAllGames();
  }

  // ---------------------------------------------------------------------------
  getGameConfiguration(gameId: number) {
    return this.datastoreService.getGame(gameId);
  }

  // ---------------------------------------------------------------------------
  async setEnvironment(environment: string) {
    return this.datastoreService.setEnvironment(environment);
  }

  // ---------------------------------------------------------------------------
  resetDisplayedPromotions() {
    return this.persistentStorage.resetDisplayedPromotions();
  }

  // ---------------------------------------------------------------------------
  // ExposableService
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  get token(): string {
    return kNameOfTesterWindowService;
  }

  // ---------------------------------------------------------------------------
  // OWWindowsListenerDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  onWindowClosed(windowName: string) {
    if (!windowName.endsWith(kTesterWindowName)) {
      return;
    }

    this.owWindowListener.removeListener(this);
    window.close();
  }
}
